import React from 'react';
import type { NPCModelProps, NPCModelRegistry } from './types';

// ==================== VICTORIAN GARDEN NPCs ====================

// Alice - the curious girl in blue dress
function AliceModel({ world, hovered }: NPCModelProps) {
  const isSpectral = world === 'spectral';
  const dressColor = isSpectral ? '#4a7ab8' : '#5c9dd8';
  const apronColor = isSpectral ? '#cccccc' : '#ffffff';
  const hairColor = isSpectral ? '#c4a35a' : '#f4d35e';
  const skinColor = isSpectral ? '#d4b896' : '#ffe4c4';

  return (
    <group>
      {/* Legs */}
      <mesh position={[-0.08, 0.15, 0]} castShadow>
        <cylinderGeometry args={[0.04, 0.04, 0.3, 8]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>
      <mesh position={[0.08, 0.15, 0]} castShadow>
        <cylinderGeometry args={[0.04, 0.04, 0.3, 8]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>
      {/* Shoes */}
      <mesh position={[-0.08, 0.02, 0.02]} castShadow>
        <boxGeometry args={[0.08, 0.04, 0.12]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      <mesh position={[0.08, 0.02, 0.02]} castShadow>
        <boxGeometry args={[0.08, 0.04, 0.12]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      {/* Dress */}
      <mesh position={[0, 0.4, 0]} castShadow>
        <coneGeometry args={[0.25, 0.4, 16]} />
        <meshStandardMaterial color={dressColor} />
      </mesh>
      {/* White apron */}
      <mesh position={[0, 0.4, 0.08]} castShadow>
        <boxGeometry args={[0.2, 0.35, 0.02]} />
        <meshStandardMaterial color={apronColor} />
      </mesh>
      {/* Torso */}
      <mesh position={[0, 0.6, 0]} castShadow>
        <boxGeometry args={[0.2, 0.15, 0.12]} />
        <meshStandardMaterial color={dressColor} />
      </mesh>
      {/* Arms */}
      <mesh position={[-0.15, 0.55, 0]} rotation={[0, 0, 0.3]} castShadow>
        <cylinderGeometry args={[0.03, 0.03, 0.2, 8]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>
      <mesh position={[0.15, 0.55, 0]} rotation={[0, 0, -0.3]} castShadow>
        <cylinderGeometry args={[0.03, 0.03, 0.2, 8]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.78, 0]} castShadow>
        <sphereGeometry args={[0.12, 16, 16]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>
      {/* Hair */}
      <mesh position={[0, 0.82, -0.02]} castShadow>
        <sphereGeometry args={[0.13, 16, 16, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>
      {/* Hair sides */}
      <mesh position={[-0.1, 0.72, 0]} castShadow>
        <boxGeometry args={[0.04, 0.15, 0.08]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>
      <mesh position={[0.1, 0.72, 0]} castShadow>
        <boxGeometry args={[0.04, 0.15, 0.08]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>
      {/* Headband */}
      <mesh position={[0, 0.88, 0]} castShadow>
        <torusGeometry args={[0.1, 0.015, 8, 16]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      {/* Bow */}
      <mesh position={[0, 0.9, -0.05]} castShadow>
        <boxGeometry args={[0.12, 0.06, 0.02]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      {/* Eyes */}
      <mesh position={[-0.04, 0.8, 0.1]} castShadow>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshStandardMaterial color="#4a90d9" />
      </mesh>
      <mesh position={[0.04, 0.8, 0.1]} castShadow>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshStandardMaterial color="#4a90d9" />
      </mesh>
      {/* Hover glow */}
      {hovered && (
        <pointLight position={[0, 0.5, 0]} intensity={0.5} color="#87ceeb" distance={2} />
      )}
    </group>
  );
}

// White Rabbit - always late, with pocket watch
function WhiteRabbitModel({ world, hovered }: NPCModelProps) {
  const isSpectral = world === 'spectral';
  const furColor = isSpectral ? '#d8d8e8' : '#ffffff';
  const coatColor = isSpectral ? '#8b2030' : '#c41e3a';
  const eyeColor = isSpectral ? '#cc6699' : '#ff99cc';

  return (
    <group>
      {/* Feet */}
      <mesh position={[-0.08, 0.05, 0.05]} rotation={[0.3, 0, 0]} castShadow>
        <boxGeometry args={[0.1, 0.06, 0.18]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      <mesh position={[0.08, 0.05, 0.05]} rotation={[0.3, 0, 0]} castShadow>
        <boxGeometry args={[0.1, 0.06, 0.18]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Legs */}
      <mesh position={[-0.06, 0.18, 0]} castShadow>
        <cylinderGeometry args={[0.04, 0.05, 0.2, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      <mesh position={[0.06, 0.18, 0]} castShadow>
        <cylinderGeometry args={[0.04, 0.05, 0.2, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Body */}
      <mesh position={[0, 0.38, 0]} castShadow>
        <sphereGeometry args={[0.15, 16, 16]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Red waistcoat */}
      <mesh position={[0, 0.4, 0.05]} castShadow>
        <boxGeometry args={[0.22, 0.2, 0.08]} />
        <meshStandardMaterial color={coatColor} />
      </mesh>
      {/* Gold buttons */}
      {[0.08, 0, -0.08].map((y, i) => (
        <mesh key={i} position={[0, 0.35 + y * 0.4, 0.1]} castShadow>
          <sphereGeometry args={[0.015, 8, 8]} />
          <meshStandardMaterial color="#ffd700" metalness={0.8} roughness={0.2} />
        </mesh>
      ))}
      {/* Arms */}
      <mesh position={[-0.18, 0.4, 0]} rotation={[0, 0, 0.5]} castShadow>
        <cylinderGeometry args={[0.03, 0.03, 0.15, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      <mesh position={[0.18, 0.4, 0]} rotation={[0, 0, -0.5]} castShadow>
        <cylinderGeometry args={[0.03, 0.03, 0.15, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.6, 0]} castShadow>
        <sphereGeometry args={[0.12, 16, 16]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Snout */}
      <mesh position={[0, 0.57, 0.1]} castShadow>
        <sphereGeometry args={[0.05, 12, 12]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Pink nose */}
      <mesh position={[0, 0.58, 0.14]} castShadow>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshStandardMaterial color="#ffb6c1" />
      </mesh>
      {/* Long ears */}
      <mesh position={[-0.06, 0.85, -0.02]} rotation={[0.2, 0, -0.1]} castShadow>
        <cylinderGeometry args={[0.02, 0.03, 0.3, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      <mesh position={[0.06, 0.85, -0.02]} rotation={[0.2, 0, 0.1]} castShadow>
        <cylinderGeometry args={[0.02, 0.03, 0.3, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Inner ears (pink) */}
      <mesh position={[-0.06, 0.85, 0]} rotation={[0.2, 0, -0.1]} castShadow>
        <cylinderGeometry args={[0.01, 0.02, 0.28, 8]} />
        <meshStandardMaterial color="#ffb6c1" />
      </mesh>
      <mesh position={[0.06, 0.85, 0]} rotation={[0.2, 0, 0.1]} castShadow>
        <cylinderGeometry args={[0.01, 0.02, 0.28, 8]} />
        <meshStandardMaterial color="#ffb6c1" />
      </mesh>
      {/* Eyes */}
      <mesh position={[-0.04, 0.62, 0.08]} castShadow>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshStandardMaterial color={eyeColor} />
      </mesh>
      <mesh position={[0.04, 0.62, 0.08]} castShadow>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshStandardMaterial color={eyeColor} />
      </mesh>
      {/* Pocket watch in hand */}
      <mesh position={[0.22, 0.35, 0.05]} castShadow>
        <cylinderGeometry args={[0.04, 0.04, 0.01, 16]} />
        <meshStandardMaterial color="#ffd700" metalness={0.8} roughness={0.2} />
      </mesh>
      <mesh position={[0.22, 0.35, 0.06]} castShadow>
        <cylinderGeometry args={[0.03, 0.03, 0.01, 16]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      {/* Hover glow */}
      {hovered && (
        <pointLight position={[0, 0.5, 0]} intensity={0.5} color="#ffd700" distance={2} />
      )}
    </group>
  );
}

// Queen of Hearts - the tyrannical queen
function QueenOfHeartsModel({ world, hovered }: NPCModelProps) {
  const isSpectral = world === 'spectral';
  const dressColor = isSpectral ? '#8b0020' : '#b01030';
  const skinColor = isSpectral ? '#e8d8c8' : '#ffe4c4';
  const crownColor = isSpectral ? '#b8860b' : '#ffd700';

  return (
    <group>
      {/* Wide dress base */}
      <mesh position={[0, 0.25, 0]} castShadow>
        <coneGeometry args={[0.4, 0.5, 16]} />
        <meshStandardMaterial color={dressColor} />
      </mesh>
      {/* Hearts on dress */}
      {[[-0.15, 0.2, 0.25], [0.15, 0.2, 0.25], [0, 0.35, 0.28]].map((pos, i) => (
        <mesh key={i} position={pos as [number, number, number]} castShadow>
          <sphereGeometry args={[0.04, 8, 8]} />
          <meshStandardMaterial color="#ffffff" />
        </mesh>
      ))}
      {/* Upper body */}
      <mesh position={[0, 0.55, 0]} castShadow>
        <boxGeometry args={[0.25, 0.2, 0.15]} />
        <meshStandardMaterial color={dressColor} />
      </mesh>
      {/* Large collar */}
      <mesh position={[0, 0.65, 0]} rotation={[Math.PI / 2, 0, 0]} castShadow>
        <torusGeometry args={[0.18, 0.05, 8, 16]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      {/* Arms */}
      <mesh position={[-0.22, 0.55, 0]} rotation={[0, 0, 0.6]} castShadow>
        <cylinderGeometry args={[0.04, 0.04, 0.2, 8]} />
        <meshStandardMaterial color={dressColor} />
      </mesh>
      <mesh position={[0.22, 0.55, 0]} rotation={[0, 0, -0.6]} castShadow>
        <cylinderGeometry args={[0.04, 0.04, 0.2, 8]} />
        <meshStandardMaterial color={dressColor} />
      </mesh>
      {/* Hands */}
      <mesh position={[-0.3, 0.48, 0]} castShadow>
        <sphereGeometry args={[0.04, 8, 8]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>
      <mesh position={[0.3, 0.48, 0]} castShadow>
        <sphereGeometry args={[0.04, 8, 8]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.78, 0]} castShadow>
        <sphereGeometry args={[0.12, 16, 16]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>
      {/* Black hair */}
      <mesh position={[0, 0.82, -0.02]} castShadow>
        <sphereGeometry args={[0.13, 16, 16, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      {/* Hair buns */}
      <mesh position={[-0.12, 0.85, 0]} castShadow>
        <sphereGeometry args={[0.05, 8, 8]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      <mesh position={[0.12, 0.85, 0]} castShadow>
        <sphereGeometry args={[0.05, 8, 8]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      {/* Crown */}
      <mesh position={[0, 0.95, 0]} castShadow>
        <cylinderGeometry args={[0.1, 0.12, 0.08, 8]} />
        <meshStandardMaterial color={crownColor} metalness={0.8} roughness={0.2} />
      </mesh>
      {/* Crown points */}
      {[0, 1, 2, 3, 4].map((i) => (
        <mesh
          key={i}
          position={[
            Math.cos((i / 5) * Math.PI * 2) * 0.09,
            1.02,
            Math.sin((i / 5) * Math.PI * 2) * 0.09
          ]}
          castShadow
        >
          <coneGeometry args={[0.02, 0.06, 4]} />
          <meshStandardMaterial color={crownColor} metalness={0.8} roughness={0.2} />
        </mesh>
      ))}
      {/* Heart gem on crown */}
      <mesh position={[0, 0.98, 0.1]} castShadow>
        <sphereGeometry args={[0.025, 8, 8]} />
        <meshStandardMaterial color="#ff0040" emissive="#ff0040" emissiveIntensity={0.3} />
      </mesh>
      {/* Angry eyebrows */}
      <mesh position={[-0.04, 0.82, 0.1]} rotation={[0, 0, 0.3]} castShadow>
        <boxGeometry args={[0.04, 0.01, 0.01]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      <mesh position={[0.04, 0.82, 0.1]} rotation={[0, 0, -0.3]} castShadow>
        <boxGeometry args={[0.04, 0.01, 0.01]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      {/* Eyes */}
      <mesh position={[-0.04, 0.79, 0.1]} castShadow>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      <mesh position={[0.04, 0.79, 0.1]} castShadow>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      {/* Red lips (frown) */}
      <mesh position={[0, 0.72, 0.1]} castShadow>
        <boxGeometry args={[0.06, 0.015, 0.01]} />
        <meshStandardMaterial color="#ff0040" />
      </mesh>
      {/* Scepter */}
      <mesh position={[0.35, 0.6, 0]} castShadow>
        <cylinderGeometry args={[0.015, 0.015, 0.5, 8]} />
        <meshStandardMaterial color={crownColor} metalness={0.7} roughness={0.3} />
      </mesh>
      <mesh position={[0.35, 0.88, 0]} castShadow>
        <sphereGeometry args={[0.04, 8, 8]} />
        <meshStandardMaterial color="#ff0040" emissive="#ff0040" emissiveIntensity={0.3} />
      </mesh>
      {/* Hover glow */}
      {hovered && (
        <pointLight position={[0, 0.5, 0]} intensity={0.5} color="#ff0040" distance={2} />
      )}
    </group>
  );
}

// Mad Hatter - the eccentric tea party host
function MadHatterModel({ world, hovered }: NPCModelProps) {
  const isSpectral = world === 'spectral';
  const coatColor = isSpectral ? '#2a4858' : '#2e8b57';
  const hatColor = isSpectral ? '#3a2820' : '#4a3728';
  const skinColor = isSpectral ? '#d8c8b8' : '#ffe4c4';
  const hairColor = isSpectral ? '#c85828' : '#ff6b35';

  return (
    <group>
      {/* Feet/shoes */}
      <mesh position={[-0.08, 0.05, 0.03]} castShadow>
        <boxGeometry args={[0.1, 0.08, 0.15]} />
        <meshStandardMaterial color="#3a2820" />
      </mesh>
      <mesh position={[0.08, 0.05, 0.03]} castShadow>
        <boxGeometry args={[0.1, 0.08, 0.15]} />
        <meshStandardMaterial color="#3a2820" />
      </mesh>
      {/* Legs */}
      <mesh position={[-0.06, 0.22, 0]} castShadow>
        <cylinderGeometry args={[0.035, 0.04, 0.3, 8]} />
        <meshStandardMaterial color="#6a5a4a" />
      </mesh>
      <mesh position={[0.06, 0.22, 0]} castShadow>
        <cylinderGeometry args={[0.035, 0.04, 0.3, 8]} />
        <meshStandardMaterial color="#6a5a4a" />
      </mesh>
      {/* Torso */}
      <mesh position={[0, 0.48, 0]} castShadow>
        <boxGeometry args={[0.22, 0.25, 0.12]} />
        <meshStandardMaterial color={coatColor} />
      </mesh>
      {/* Coat tails */}
      <mesh position={[0, 0.35, -0.08]} castShadow>
        <boxGeometry args={[0.2, 0.15, 0.04]} />
        <meshStandardMaterial color={coatColor} />
      </mesh>
      {/* Bow tie */}
      <mesh position={[0, 0.58, 0.07]} castShadow>
        <boxGeometry args={[0.1, 0.04, 0.02]} />
        <meshStandardMaterial color="#ff6b35" />
      </mesh>
      {/* Arms */}
      <mesh position={[-0.18, 0.5, 0]} rotation={[0, 0, 0.4]} castShadow>
        <cylinderGeometry args={[0.03, 0.03, 0.22, 8]} />
        <meshStandardMaterial color={coatColor} />
      </mesh>
      <mesh position={[0.18, 0.5, 0]} rotation={[0, 0, -0.4]} castShadow>
        <cylinderGeometry args={[0.03, 0.03, 0.22, 8]} />
        <meshStandardMaterial color={coatColor} />
      </mesh>
      {/* Hands */}
      <mesh position={[-0.26, 0.42, 0]} castShadow>
        <sphereGeometry args={[0.035, 8, 8]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>
      <mesh position={[0.26, 0.42, 0]} castShadow>
        <sphereGeometry args={[0.035, 8, 8]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.72, 0]} castShadow>
        <sphereGeometry args={[0.11, 16, 16]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>
      {/* Wild orange hair */}
      <mesh position={[-0.1, 0.72, 0]} castShadow>
        <sphereGeometry args={[0.06, 8, 8]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>
      <mesh position={[0.1, 0.72, 0]} castShadow>
        <sphereGeometry args={[0.06, 8, 8]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>
      <mesh position={[0, 0.72, -0.08]} castShadow>
        <sphereGeometry args={[0.07, 8, 8]} />
        <meshStandardMaterial color={hairColor} />
      </mesh>
      {/* Top hat */}
      <mesh position={[0, 0.88, 0]} castShadow>
        <cylinderGeometry args={[0.12, 0.12, 0.04, 16]} />
        <meshStandardMaterial color={hatColor} />
      </mesh>
      <mesh position={[0, 1.05, 0]} castShadow>
        <cylinderGeometry args={[0.09, 0.1, 0.3, 16]} />
        <meshStandardMaterial color={hatColor} />
      </mesh>
      {/* Hat band */}
      <mesh position={[0, 0.93, 0]} castShadow>
        <cylinderGeometry args={[0.095, 0.095, 0.04, 16]} />
        <meshStandardMaterial color="#8b4513" />
      </mesh>
      {/* 10/6 card in hat */}
      <mesh position={[0.08, 1.0, 0.08]} rotation={[0, 0.5, 0.2]} castShadow>
        <boxGeometry args={[0.05, 0.04, 0.005]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      {/* Big eyes */}
      <mesh position={[-0.04, 0.74, 0.09]} castShadow>
        <sphereGeometry args={[0.025, 8, 8]} />
        <meshStandardMaterial color="#7cfc00" />
      </mesh>
      <mesh position={[0.04, 0.74, 0.09]} castShadow>
        <sphereGeometry args={[0.025, 8, 8]} />
        <meshStandardMaterial color="#7cfc00" />
      </mesh>
      {/* Big grin */}
      <mesh position={[0, 0.66, 0.08]} rotation={[0, 0, 0]} castShadow>
        <torusGeometry args={[0.04, 0.01, 8, 16, Math.PI]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      {/* Teacup in hand */}
      <mesh position={[0.28, 0.45, 0.05]} castShadow>
        <cylinderGeometry args={[0.03, 0.025, 0.04, 12]} />
        <meshStandardMaterial color="#f5f5dc" />
      </mesh>
      {/* Hover glow */}
      {hovered && (
        <pointLight position={[0, 0.5, 0]} intensity={0.5} color="#7cfc00" distance={2} />
      )}
    </group>
  );
}

// Cheshire Cat - the grinning cat
function CheshireCatModel({ world, hovered }: NPCModelProps) {
  const isSpectral = world === 'spectral';
  const furColor = isSpectral ? '#d858d8' : '#ff69b4';
  const stripeColor = isSpectral ? '#6828a8' : '#9932cc';

  return (
    <group>
      {/* Back legs */}
      <mesh position={[-0.12, 0.1, -0.15]} castShadow>
        <sphereGeometry args={[0.08, 12, 12]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      <mesh position={[0.12, 0.1, -0.15]} castShadow>
        <sphereGeometry args={[0.08, 12, 12]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Front paws */}
      <mesh position={[-0.08, 0.08, 0.15]} castShadow>
        <sphereGeometry args={[0.05, 8, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      <mesh position={[0.08, 0.08, 0.15]} castShadow>
        <sphereGeometry args={[0.05, 8, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Body */}
      <mesh position={[0, 0.18, 0]} rotation={[0.2, 0, 0]} castShadow>
        <capsuleGeometry args={[0.1, 0.25, 8, 16]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Stripes on body */}
      {[-0.1, 0, 0.1].map((z, i) => (
        <mesh key={i} position={[0, 0.22, z]} rotation={[0.2, 0, Math.PI / 2]} castShadow>
          <torusGeometry args={[0.1, 0.015, 8, 16]} />
          <meshStandardMaterial color={stripeColor} />
        </mesh>
      ))}
      {/* Head */}
      <mesh position={[0, 0.38, 0.18]} castShadow>
        <sphereGeometry args={[0.12, 16, 16]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Ears */}
      <mesh position={[-0.08, 0.52, 0.15]} rotation={[0.3, 0, -0.2]} castShadow>
        <coneGeometry args={[0.04, 0.08, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      <mesh position={[0.08, 0.52, 0.15]} rotation={[0.3, 0, 0.2]} castShadow>
        <coneGeometry args={[0.04, 0.08, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Big glowing eyes */}
      <mesh position={[-0.05, 0.42, 0.28]} castShadow>
        <sphereGeometry args={[0.03, 8, 8]} />
        <meshStandardMaterial color="#ffff00" emissive="#ffff00" emissiveIntensity={0.5} />
      </mesh>
      <mesh position={[0.05, 0.42, 0.28]} castShadow>
        <sphereGeometry args={[0.03, 8, 8]} />
        <meshStandardMaterial color="#ffff00" emissive="#ffff00" emissiveIntensity={0.5} />
      </mesh>
      {/* Pupils */}
      <mesh position={[-0.05, 0.42, 0.31]} castShadow>
        <sphereGeometry args={[0.012, 8, 8]} />
        <meshStandardMaterial color="#000000" />
      </mesh>
      <mesh position={[0.05, 0.42, 0.31]} castShadow>
        <sphereGeometry args={[0.012, 8, 8]} />
        <meshStandardMaterial color="#000000" />
      </mesh>
      {/* Big grin */}
      <mesh position={[0, 0.34, 0.26]} castShadow>
        <torusGeometry args={[0.08, 0.015, 8, 16, Math.PI]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      {/* Teeth */}
      {[-0.06, -0.03, 0, 0.03, 0.06].map((x, i) => (
        <mesh key={i} position={[x, 0.32, 0.28]} castShadow>
          <boxGeometry args={[0.015, 0.02, 0.01]} />
          <meshStandardMaterial color="#ffffff" />
        </mesh>
      ))}
      {/* Curly tail */}
      <mesh position={[0, 0.25, -0.25]} rotation={[0.5, 0, 0]} castShadow>
        <torusGeometry args={[0.08, 0.025, 8, 16, Math.PI * 1.5]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Tail stripes */}
      <mesh position={[0, 0.22, -0.32]} rotation={[0.5, 0, Math.PI / 2]} castShadow>
        <torusGeometry args={[0.025, 0.01, 8, 16]} />
        <meshStandardMaterial color={stripeColor} />
      </mesh>
      {/* Ghostly glow - cat can disappear */}
      {hovered && (
        <pointLight position={[0, 0.3, 0]} intensity={0.8} color="#ff69b4" distance={2} />
      )}
    </group>
  );
}

// March Hare - the mad hare at the tea party
function MarchHareModel({ world, hovered }: NPCModelProps) {
  const isSpectral = world === 'spectral';
  const furColor = isSpectral ? '#9a7b5a' : '#d2b48c';
  const vestColor = isSpectral ? '#6a4a2a' : '#8b4513';

  return (
    <group>
      {/* Feet */}
      <mesh position={[-0.08, 0.05, 0.08]} rotation={[0.4, 0, 0]} castShadow>
        <boxGeometry args={[0.08, 0.05, 0.18]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      <mesh position={[0.08, 0.05, 0.08]} rotation={[0.4, 0, 0]} castShadow>
        <boxGeometry args={[0.08, 0.05, 0.18]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Legs */}
      <mesh position={[-0.06, 0.2, 0]} castShadow>
        <cylinderGeometry args={[0.04, 0.05, 0.22, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      <mesh position={[0.06, 0.2, 0]} castShadow>
        <cylinderGeometry args={[0.04, 0.05, 0.22, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Body */}
      <mesh position={[0, 0.42, 0]} castShadow>
        <sphereGeometry args={[0.14, 16, 16]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Vest */}
      <mesh position={[0, 0.44, 0.05]} castShadow>
        <boxGeometry args={[0.2, 0.18, 0.06]} />
        <meshStandardMaterial color={vestColor} />
      </mesh>
      {/* Arms */}
      <mesh position={[-0.16, 0.42, 0]} rotation={[0, 0, 0.6]} castShadow>
        <cylinderGeometry args={[0.03, 0.03, 0.18, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      <mesh position={[0.16, 0.42, 0]} rotation={[0, 0, -0.6]} castShadow>
        <cylinderGeometry args={[0.03, 0.03, 0.18, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.62, 0]} castShadow>
        <sphereGeometry args={[0.11, 16, 16]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Snout */}
      <mesh position={[0, 0.58, 0.08]} castShadow>
        <sphereGeometry args={[0.05, 10, 10]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Nose */}
      <mesh position={[0, 0.6, 0.12]} castShadow>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshStandardMaterial color="#4a3020" />
      </mesh>
      {/* Very long crazy ears */}
      <mesh position={[-0.05, 0.88, -0.02]} rotation={[0.1, 0, -0.3]} castShadow>
        <cylinderGeometry args={[0.015, 0.03, 0.4, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      <mesh position={[0.05, 0.9, -0.02]} rotation={[-0.2, 0, 0.4]} castShadow>
        <cylinderGeometry args={[0.015, 0.03, 0.4, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Inner ears */}
      <mesh position={[-0.05, 0.88, 0]} rotation={[0.1, 0, -0.3]} castShadow>
        <cylinderGeometry args={[0.008, 0.02, 0.38, 8]} />
        <meshStandardMaterial color="#d8a8a8" />
      </mesh>
      <mesh position={[0.05, 0.9, 0]} rotation={[-0.2, 0, 0.4]} castShadow>
        <cylinderGeometry args={[0.008, 0.02, 0.38, 8]} />
        <meshStandardMaterial color="#d8a8a8" />
      </mesh>
      {/* Crazy eyes */}
      <mesh position={[-0.04, 0.65, 0.08]} castShadow>
        <sphereGeometry args={[0.025, 8, 8]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      <mesh position={[0.04, 0.65, 0.08]} castShadow>
        <sphereGeometry args={[0.025, 8, 8]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      {/* Pupils looking different directions */}
      <mesh position={[-0.045, 0.66, 0.1]} castShadow>
        <sphereGeometry args={[0.012, 6, 6]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      <mesh position={[0.035, 0.64, 0.1]} castShadow>
        <sphereGeometry args={[0.012, 6, 6]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      {/* Buck teeth */}
      <mesh position={[-0.015, 0.54, 0.1]} castShadow>
        <boxGeometry args={[0.02, 0.03, 0.01]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      <mesh position={[0.015, 0.54, 0.1]} castShadow>
        <boxGeometry args={[0.02, 0.03, 0.01]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      {/* Hover glow */}
      {hovered && (
        <pointLight position={[0, 0.5, 0]} intensity={0.5} color="#d2b48c" distance={2} />
      )}
    </group>
  );
}

// Caterpillar - the hookah-smoking caterpillar
function CaterpillarModel({ world, hovered }: NPCModelProps) {
  const isSpectral = world === 'spectral';
  const bodyColor = isSpectral ? '#4848a8' : '#4169e1';
  const accentColor = isSpectral ? '#886888' : '#9370db';

  return (
    <group>
      {/* Body segments */}
      {[0, 0.12, 0.24, 0.36, 0.48].map((z, i) => (
        <mesh key={i} position={[0, 0.12 + i * 0.02, -z]} castShadow>
          <sphereGeometry args={[0.1 - i * 0.01, 12, 12]} />
          <meshStandardMaterial color={i % 2 === 0 ? bodyColor : accentColor} />
        </mesh>
      ))}
      {/* Head (raised up) */}
      <mesh position={[0, 0.35, 0.08]} castShadow>
        <sphereGeometry args={[0.09, 12, 12]} />
        <meshStandardMaterial color={bodyColor} />
      </mesh>
      {/* Many legs */}
      {[0, 0.12, 0.24, 0.36].map((z, i) => (
        <React.Fragment key={i}>
          <mesh position={[-0.08, 0.05, -z]} rotation={[0, 0, -0.4]} castShadow>
            <cylinderGeometry args={[0.015, 0.015, 0.1, 6]} />
            <meshStandardMaterial color={accentColor} />
          </mesh>
          <mesh position={[0.08, 0.05, -z]} rotation={[0, 0, 0.4]} castShadow>
            <cylinderGeometry args={[0.015, 0.015, 0.1, 6]} />
            <meshStandardMaterial color={accentColor} />
          </mesh>
        </React.Fragment>
      ))}
      {/* Antennae */}
      <mesh position={[-0.04, 0.48, 0.1]} rotation={[0.3, 0, -0.2]} castShadow>
        <cylinderGeometry args={[0.008, 0.008, 0.12, 6]} />
        <meshStandardMaterial color={bodyColor} />
      </mesh>
      <mesh position={[0.04, 0.48, 0.1]} rotation={[0.3, 0, 0.2]} castShadow>
        <cylinderGeometry args={[0.008, 0.008, 0.12, 6]} />
        <meshStandardMaterial color={bodyColor} />
      </mesh>
      {/* Eyes */}
      <mesh position={[-0.03, 0.38, 0.15]} castShadow>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshStandardMaterial color="#ffff00" />
      </mesh>
      <mesh position={[0.03, 0.38, 0.15]} castShadow>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshStandardMaterial color="#ffff00" />
      </mesh>
      {/* Hookah */}
      <mesh position={[0.15, 0.1, 0.1]} castShadow>
        <sphereGeometry args={[0.06, 12, 12]} />
        <meshStandardMaterial color="#c0c0c0" metalness={0.6} roughness={0.4} />
      </mesh>
      <mesh position={[0.15, 0.2, 0.1]} castShadow>
        <cylinderGeometry args={[0.02, 0.03, 0.1, 8]} />
        <meshStandardMaterial color="#ffd700" metalness={0.7} roughness={0.3} />
      </mesh>
      {/* Hookah hose */}
      <mesh position={[0.08, 0.32, 0.12]} rotation={[0, 0, 0.5]} castShadow>
        <torusGeometry args={[0.06, 0.01, 8, 16, Math.PI]} />
        <meshStandardMaterial color="#8b4513" />
      </mesh>
      {/* Smoke puffs */}
      {[0.4, 0.5, 0.6].map((y, i) => (
        <mesh key={i} position={[0.1, y, 0.15]} castShadow>
          <sphereGeometry args={[0.03 + i * 0.01, 8, 8]} />
          <meshStandardMaterial color="#ffffff" transparent opacity={0.5 - i * 0.1} />
        </mesh>
      ))}
      {/* Hover glow */}
      {hovered && (
        <pointLight position={[0, 0.3, 0]} intensity={0.5} color="#9370db" distance={2} />
      )}
    </group>
  );
}

// Tweedledee and Tweedledum - the twin boys
function TweedlesModel({ world, hovered }: NPCModelProps) {
  const isSpectral = world === 'spectral';
  const shirtColor = isSpectral ? '#8b2020' : '#dc143c';
  const skinColor = isSpectral ? '#d8c8b8' : '#ffe4c4';

  const renderTweedle = (xOffset: number, mirror: boolean) => (
    <group position={[xOffset, 0, 0]}>
      {/* Round body */}
      <mesh position={[0, 0.35, 0]} castShadow>
        <sphereGeometry args={[0.2, 16, 16]} />
        <meshStandardMaterial color={shirtColor} />
      </mesh>
      {/* White collar */}
      <mesh position={[0, 0.5, 0]} rotation={[Math.PI / 2, 0, 0]} castShadow>
        <torusGeometry args={[0.12, 0.03, 8, 16]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      {/* Short legs */}
      <mesh position={[mirror ? 0.06 : -0.06, 0.1, 0]} castShadow>
        <cylinderGeometry args={[0.04, 0.05, 0.2, 8]} />
        <meshStandardMaterial color="#4a4a8a" />
      </mesh>
      <mesh position={[mirror ? -0.06 : 0.06, 0.1, 0]} castShadow>
        <cylinderGeometry args={[0.04, 0.05, 0.2, 8]} />
        <meshStandardMaterial color="#4a4a8a" />
      </mesh>
      {/* Arms */}
      <mesh position={[mirror ? 0.2 : -0.2, 0.4, 0]} rotation={[0, 0, mirror ? -0.8 : 0.8]} castShadow>
        <cylinderGeometry args={[0.03, 0.03, 0.15, 8]} />
        <meshStandardMaterial color={shirtColor} />
      </mesh>
      <mesh position={[mirror ? -0.2 : 0.2, 0.4, 0]} rotation={[0, 0, mirror ? 0.8 : -0.8]} castShadow>
        <cylinderGeometry args={[0.03, 0.03, 0.15, 8]} />
        <meshStandardMaterial color={shirtColor} />
      </mesh>
      {/* Round head */}
      <mesh position={[0, 0.62, 0]} castShadow>
        <sphereGeometry args={[0.1, 16, 16]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>
      {/* Propeller beanie */}
      <mesh position={[0, 0.72, 0]} castShadow>
        <sphereGeometry args={[0.08, 12, 12, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color={mirror ? '#4169e1' : '#ff6347'} />
      </mesh>
      {/* Propeller */}
      <mesh position={[0, 0.78, 0]} rotation={[0, mirror ? 0.5 : -0.5, Math.PI / 2]} castShadow>
        <boxGeometry args={[0.12, 0.02, 0.02]} />
        <meshStandardMaterial color="#ffd700" />
      </mesh>
      {/* Eyes */}
      <mesh position={[-0.03, 0.64, 0.08]} castShadow>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      <mesh position={[0.03, 0.64, 0.08]} castShadow>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      {/* Rosy cheeks */}
      <mesh position={[-0.06, 0.6, 0.06]} castShadow>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshStandardMaterial color="#ffb6c1" />
      </mesh>
      <mesh position={[0.06, 0.6, 0.06]} castShadow>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshStandardMaterial color="#ffb6c1" />
      </mesh>
    </group>
  );

  return (
    <group>
      {renderTweedle(-0.22, false)}
      {renderTweedle(0.22, true)}
      {/* Hover glow */}
      {hovered && (
        <pointLight position={[0, 0.5, 0]} intensity={0.5} color="#ffd700" distance={2} />
      )}
    </group>
  );
}

// Card Guard - playing card soldier
function CardGuardModel({ world, hovered }: NPCModelProps) {
  const isSpectral = world === 'spectral';
  const cardColor = isSpectral ? '#c8c8c8' : '#ffffff';
  const suitColor = isSpectral ? '#8b0020' : '#ff0040';

  return (
    <group>
      {/* Flat card body */}
      <mesh position={[0, 0.45, 0]} castShadow>
        <boxGeometry args={[0.35, 0.7, 0.05]} />
        <meshStandardMaterial color={cardColor} />
      </mesh>
      {/* Heart suit symbol */}
      <mesh position={[0, 0.5, 0.03]} castShadow>
        <sphereGeometry args={[0.08, 8, 8]} />
        <meshStandardMaterial color={suitColor} />
      </mesh>
      {/* Legs (thin) */}
      <mesh position={[-0.08, 0.08, 0]} castShadow>
        <boxGeometry args={[0.06, 0.16, 0.04]} />
        <meshStandardMaterial color={cardColor} />
      </mesh>
      <mesh position={[0.08, 0.08, 0]} castShadow>
        <boxGeometry args={[0.06, 0.16, 0.04]} />
        <meshStandardMaterial color={cardColor} />
      </mesh>
      {/* Arms holding spear */}
      <mesh position={[-0.22, 0.5, 0]} rotation={[0, 0, 0.3]} castShadow>
        <boxGeometry args={[0.05, 0.25, 0.04]} />
        <meshStandardMaterial color={cardColor} />
      </mesh>
      <mesh position={[0.22, 0.5, 0]} rotation={[0, 0, -0.3]} castShadow>
        <boxGeometry args={[0.05, 0.25, 0.04]} />
        <meshStandardMaterial color={cardColor} />
      </mesh>
      {/* Flat head */}
      <mesh position={[0, 0.88, 0]} castShadow>
        <boxGeometry args={[0.2, 0.16, 0.04]} />
        <meshStandardMaterial color={cardColor} />
      </mesh>
      {/* Simple face */}
      <mesh position={[-0.04, 0.9, 0.025]} castShadow>
        <sphereGeometry args={[0.02, 6, 6]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      <mesh position={[0.04, 0.9, 0.025]} castShadow>
        <sphereGeometry args={[0.02, 6, 6]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      {/* Spear */}
      <mesh position={[0.3, 0.6, 0]} castShadow>
        <cylinderGeometry args={[0.015, 0.015, 0.9, 8]} />
        <meshStandardMaterial color="#8b4513" />
      </mesh>
      <mesh position={[0.3, 1.08, 0]} castShadow>
        <coneGeometry args={[0.03, 0.08, 6]} />
        <meshStandardMaterial color="#c0c0c0" metalness={0.8} roughness={0.2} />
      </mesh>
      {/* Hover glow */}
      {hovered && (
        <pointLight position={[0, 0.5, 0]} intensity={0.5} color="#ff0040" distance={2} />
      )}
    </group>
  );
}

// ==================== EXPORT REGISTRY ====================

export const wonderlandNPCs: NPCModelRegistry = {
  alice: AliceModel,
  white_rabbit: WhiteRabbitModel,
  queen_of_hearts: QueenOfHeartsModel,
  mad_hatter: MadHatterModel,
  cheshire_cat: CheshireCatModel,
  march_hare: MarchHareModel,
  caterpillar: CaterpillarModel,
  tweedles: TweedlesModel,
  card_guard: CardGuardModel,
};
